import { forwardRef, type ComponentPropsWithoutRef } from 'react'

import { cn } from './cn'
import { useControlProps, useQuietField } from './control-scope'
import { controlShell, EMPTY_MARK, Field } from './Field'

type Shared = {
  label?: string
  helper?: string
  error?: string
  className?: string
  /** Datasheet row: no box until hover or focus. */
  quiet?: boolean
  /** Empty input reads as `null`, so a cleared field is not an empty string. */
  onValueChange?: (value: string | null) => void
}

type TextInputProps = Shared &
  Omit<ComponentPropsWithoutRef<'input'>, 'onChange' | 'className' | 'type'> & {
    /** A line of text of some kind. A day is `DateInput`: the browser's own
     *  date field writes the day in the order of the reader's system
     *  language. */
    type?: 'text' | 'email' | 'password' | 'search' | 'tel' | 'url' | 'number'
  }

/** One line of text. */
export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  function TextInput(
    {
      label,
      helper,
      error,
      className,
      quiet: quietProp,
      onValueChange,
      ...rest
    },
    ref,
  ) {
    const quiet = useQuietField(quietProp)
    const control = useControlProps(rest)

    return (
      <Field label={label} helper={helper} error={error} className={className}>
        {field => (
          <input
            ref={ref}
            {...field}
            // An empty quiet field draws nothing at all; the dash is what says
            // "no value" rather than "no field".
            placeholder={quiet ? EMPTY_MARK : undefined}
            className={controlShell(quiet)}
            onChange={event => onValueChange?.(event.target.value || null)}
            {...control}
          />
        )}
      </Field>
    )
  },
)

type TextAreaProps = Shared &
  Omit<ComponentPropsWithoutRef<'textarea'>, 'onChange' | 'className'>

/** Several lines of text. */
export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  function TextArea(
    {
      label,
      helper,
      error,
      className,
      quiet: quietProp,
      onValueChange,
      rows = 3,
      ...rest
    },
    ref,
  ) {
    const quiet = useQuietField(quietProp)
    const control = useControlProps(rest)

    return (
      <Field label={label} helper={helper} error={error} className={className}>
        {field => (
          <textarea
            ref={ref}
            {...field}
            rows={rows}
            className={controlShell(
              quiet,
              cn(
                'h-auto py-1.5 resize-y leading-snug',
                // A quiet one-liner still reads as a value; several empty lines
                // read as nothing at all, so the frame stays — just fainter.
                quiet && 'border-line-soft',
              ),
            )}
            onChange={event => onValueChange?.(event.target.value || null)}
            {...control}
          />
        )}
      </Field>
    )
  },
)
